import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/store/appStore';
import {
  cn,
  formatNumber,
  formatPercent,
  timeAgo,
  formatDate,
  getStatusColor,
} from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { api } from '@/api/client';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Tabs } from '@/components/ui/Tabs';
import type { ActivityFeedItem, Device, Alert } from '@/types';
import {
  Server,
  Activity,
  AlertTriangle,
  ShieldAlert,
  CheckCircle2,
  RefreshCw,
  Clock,
  Cpu,
  HardDrive,
  Database,
  Play,
  UserPlus,
  Monitor,
  ShieldCheck,
  ArrowUpRight,
  ChevronRight,
  Radio,
  Layers,
  Zap,
  Terminal,
  FileCode,
  RotateCcw,
  ExternalLink,
  Info,
  SlidersHorizontal,
  Wifi,
  WifiOff,
} from 'lucide-react';

interface OverviewMetricsData {
  totalDevices: number;
  onlineDevices: number;
  offlineDevices: number;
  warningDevices: number;
  criticalDevices: number;
  needingAttention: number;
  activeAlerts: number;
  criticalAlerts: number;
  patchCompliance: number;
  antivirusActive: number;
  pendingReboots: number;
  runningJobs: number;
  recentlyEnrolled: number;
  activeRemoteSessions: number;
  avgCpu: number;
  avgMemory: number;
  avgDisk: number;
  alertTrend: Array<{ date: string; critical: number; warning: number; resolved: number }>;
  deviceStatusDistribution: Array<{ label: string; value: number; color: string }>;
}

export function Overview() {
  const navigate = useNavigate();
  const { currentTenant, wsConnected } = useAppStore();

  const emptyMetrics: OverviewMetricsData = {
    totalDevices: 0,
    onlineDevices: 0,
    offlineDevices: 0,
    warningDevices: 0,
    criticalDevices: 0,
    needingAttention: 0,
    activeAlerts: 0,
    criticalAlerts: 0,
    patchCompliance: 0,
    antivirusActive: 0,
    pendingReboots: 0,
    runningJobs: 0,
    recentlyEnrolled: 0,
    activeRemoteSessions: 0,
    avgCpu: 0,
    avgMemory: 0,
    avgDisk: 0,
    alertTrend: [],
    deviceStatusDistribution: [],
  };

  // State Management
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());

  // Data States
  const [metrics, setMetrics] = useState<OverviewMetricsData>(emptyMetrics);
  const [activityFeed, setActivityFeed] = useState<ActivityFeedItem[]>([]);
  const [recentDevices, setRecentDevices] = useState<Device[]>([]);
  const [criticalAlerts, setCriticalAlerts] = useState<Alert[]>([]);

  // Activity Feed Filter
  const [activityFilter, setActivityFilter] = useState<string>('all');
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);

  const applyMetrics = (metricsRes: any) => {
    const metricsPayload = metricsRes?.totalDevices != null
      ? metricsRes
      : (metricsRes?.data ?? metricsRes);
    setMetrics({
      ...emptyMetrics,
      ...(metricsPayload || {}),
      alertTrend: Array.isArray(metricsPayload?.alertTrend) ? metricsPayload.alertTrend : [],
      deviceStatusDistribution: Array.isArray(metricsPayload?.deviceStatusDistribution)
        ? metricsPayload.deviceStatusDistribution
        : [],
    });
    setError(null);
  };

  const metricsFromDevices = (deviceRows: Device[], total?: number) => {
    const online = deviceRows.filter((d) => d.status === 'online').length;
    const warning = deviceRows.filter((d) => d.status === 'warning').length;
    const critical = deviceRows.filter((d) => d.status === 'critical').length;
    const offline = deviceRows.filter((d) => d.status === 'offline').length;
    const pendingReboots = deviceRows.filter((d) => d.pendingReboot).length;
    const n = total && total > 0 ? total : deviceRows.length;
    setMetrics((prev) => {
      if (prev.totalDevices > 0) {
        return prev;
      }
      return {
        ...prev,
        totalDevices: n,
        onlineDevices: online,
        offlineDevices: offline,
        warningDevices: warning,
        criticalDevices: critical,
        needingAttention: warning + critical + pendingReboots,
        pendingReboots,
        avgCpu: deviceRows.length
          ? Math.round(deviceRows.reduce((s, d) => s + (Number(d.cpu) || 0), 0) / deviceRows.length)
          : 0,
        avgMemory: deviceRows.length
          ? Math.round(deviceRows.reduce((s, d) => s + (Number(d.memory) || 0), 0) / deviceRows.length)
          : 0,
        avgDisk: deviceRows.length
          ? Math.round(deviceRows.reduce((s, d) => s + (Number(d.disk) || 0), 0) / deviceRows.length)
          : 0,
        deviceStatusDistribution: [
          { label: 'Online', value: online, color: '#22c55e' },
          { label: 'Offline', value: offline, color: '#ef4444' },
          { label: 'Warning', value: warning, color: '#f59e0b' },
          { label: 'Critical', value: critical, color: '#ef4444' },
        ],
      };
    });
  };

  const handleAuthError = (err: any) => {
    if (err?.status === 401 || err?.code === 'AUTH_TOKEN_MISSING' || err?.code === 'AUTH_TOKEN_INVALID') {
      api.clearTokens();
      navigate('/login', { replace: true });
      return true;
    }
    return false;
  };

  const fetchData = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    }
    setError(null);

    const loadDevices = mockApi.getDevices({ page: 1, pageSize: 25 }).then((devicesRes: any) => {
      const deviceRows = Array.isArray(devicesRes)
        ? devicesRes
        : (Array.isArray(devicesRes?.data) ? devicesRes.data : []);
      setRecentDevices(deviceRows.slice(0, 5));
      metricsFromDevices(deviceRows, Number(devicesRes?.total) || deviceRows.length);
    });

    const loadOverview = Promise.race([
      mockApi.getOverviewMetrics(),
      new Promise((_, reject) => window.setTimeout(() => reject(new Error('overview-timeout')), 4000)),
    ]).then((metricsRes) => {
      applyMetrics(metricsRes);
    });

    const loadActivity = mockApi.getActivityFeed().then((activityRes) => {
      setActivityFeed(Array.isArray(activityRes) ? activityRes : []);
    });

    const loadAlerts = mockApi.getAlerts({ severity: 'critical' }).then((alertsRes: any) => {
      const alertRows = Array.isArray(alertsRes)
        ? alertsRes
        : (Array.isArray(alertsRes?.data) ? alertsRes.data : []);
      setCriticalAlerts(alertRows.filter((a: any) => a.state !== 'resolved'));
    });

    const results = await Promise.allSettled([loadDevices, loadOverview, loadActivity, loadAlerts]);
    for (const result of results) {
      if (result.status === 'rejected' && handleAuthError(result.reason)) {
        setRefreshing(false);
        return;
      }
    }

    setLastRefreshed(new Date());
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    fetchData();
    const timer = window.setInterval(() => {
      fetchData(true);
    }, 30000);
    return () => window.clearInterval(timer);
  }, [currentTenant]);

  // Filtered Activity Feed
  const filteredActivityFeed = activityFeed.filter((item) => {
    if (activityFilter === 'all') return true;
    if (activityFilter === 'alert') return item.type === 'alert';
    if (activityFilter === 'job') return item.type === 'job';
    if (activityFilter === 'enrollment') return item.type === 'enrollment';
    if (activityFilter === 'session') return item.type === 'session';
    return true;
  });

  // Helper for activity feed icons
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'alert':
        return <AlertTriangle className="h-4 w-4 text-[var(--status-warning)]" />;
      case 'enrollment':
        return <UserPlus className="h-4 w-4 text-[var(--status-online)]" />;
      case 'job':
        return <Play className="h-4 w-4 text-[var(--accent)]" />;
      case 'session':
        return <Monitor className="h-4 w-4 text-purple-400" />;
      case 'patch':
        return <ShieldCheck className="h-4 w-4 text-emerald-400" />;
      case 'reboot':
        return <RotateCcw className="h-4 w-4 text-amber-400" />;
      default:
        return <Activity className="h-4 w-4 text-[var(--text-muted)]" />;
    }
  };

  if (loading && !metrics) {
    return (
      <div className="p-6 space-y-6 animate-pulse">
        {/* Skeleton Header */}
        <div className="flex items-center justify-between">
          <div className="h-8 w-48 bg-[var(--elevated)] rounded-md" />
          <div className="h-8 w-32 bg-[var(--elevated)] rounded-md" />
        </div>
        {/* Skeleton Top Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-[var(--panel)] border border-[var(--border)] rounded-lg p-4 space-y-3">
              <div className="h-4 w-24 bg-[var(--elevated)] rounded" />
              <div className="h-8 w-16 bg-[var(--elevated)] rounded" />
            </div>
          ))}
        </div>
        {/* Skeleton Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-72 bg-[var(--panel)] border border-[var(--border)] rounded-lg" />
          <div className="h-72 bg-[var(--panel)] border border-[var(--border)] rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[60vh] text-center space-y-4">
        <div className="h-12 w-12 rounded-full bg-[var(--status-critical)]/15 border border-[var(--status-critical)]/30 flex items-center justify-center text-[var(--status-critical)]">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <h2 className="text-lg font-bold text-[var(--text-primary)]">Unable to load Overview dashboard</h2>
        <p className="text-xs text-[var(--text-muted)] max-w-md">{error || 'An unexpected telemetry failure occurred.'}</p>
        <Button variant="primary" onClick={() => fetchData(true)}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry Connection
        </Button>
      </div>
    );
  }

  // Calculate Max Alert Trend Count for Chart Scaling
  const maxTrendVal = Math.max(
    ...(metrics.alertTrend ?? []).map((d) => d.critical + d.warning + d.resolved),
    10
  );

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-[1600px] mx-auto text-[var(--text-primary)] font-sans">
      
      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-[var(--border)]">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">Overview</h1>
            <Badge variant="default" className="font-mono uppercase text-[2xs]">
              Tenant: {currentTenant}
            </Badge>
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded text-[2xs] bg-[var(--elevated)] border border-[var(--border)]">
              {wsConnected ? (
                <>
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--status-online)] animate-pulse" />
                  <span className="text-[var(--text-secondary)] font-mono">Live Stream</span>
                </>
              ) : (
                <>
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--status-offline)]" />
                  <span className="text-[var(--text-muted)] font-mono">Offline</span>
                </>
              )}
            </div>
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            System status, operational metrics, and infrastructure health telemetry
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right hidden md:block">
            <div className="text-xs font-medium text-[var(--text-secondary)]">
              {formatDate(new Date().toISOString(), 'EEEE, MMMM d, yyyy')}
            </div>
            <div className="text-[2xs] text-[var(--text-muted)] font-mono">
              Last updated: {lastRefreshed.toLocaleTimeString()}
            </div>
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => fetchData(true)}
            loading={refreshing}
            className="border-[var(--border)] hover:bg-[var(--hover)]"
          >
            <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', refreshing && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Active Critical Alerts Banner (If Any) ── */}
      {criticalAlerts.length > 0 && (
        <div className="bg-[var(--status-critical)]/10 border border-[var(--status-critical)]/30 rounded-lg p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-5 w-5 text-[var(--status-critical)] shrink-0 animate-pulse" />
            <div>
              <span className="font-semibold text-[var(--status-critical)]">
                {criticalAlerts.length} Unresolved Critical Alert{criticalAlerts.length > 1 ? 's' : ''} Requiring Immediate Action
              </span>
              <p className="text-[var(--text-secondary)] text-[2xs] mt-0.5">
                Latest: {criticalAlerts[0]?.hostname} — {criticalAlerts[0]?.message}
              </p>
            </div>
          </div>
          <Button
            variant="danger"
            size="sm"
            onClick={() => navigate('/alerts?severity=critical')}
            className="shrink-0 font-medium"
          >
            View Critical Alerts
            <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      )}

      {/* ── Top Row: 4 Device Stat Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Total Devices */}
        <div
          onClick={() => navigate('/devices')}
          className="bg-[var(--panel)] border border-[var(--border)] hover:border-[var(--accent)]/50 rounded-lg p-4 transition-all duration-150 cursor-pointer group relative overflow-hidden"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider">Total Devices</span>
            <div className="p-2 rounded-md bg-[var(--elevated)] text-[var(--text-secondary)] group-hover:text-[var(--accent)] transition-colors">
              <Server className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums text-[var(--text-primary)]">
              {formatNumber(metrics.totalDevices)}
            </span>
            <span className="text-2xs text-[var(--text-muted)] font-mono group-hover:text-[var(--accent)] flex items-center">
              View All <ArrowUpRight className="h-3 w-3 ml-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
          </div>
          <div className="mt-2 text-[2xs] text-[var(--text-muted)] flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
            <span>Enrolled across {currentTenant}</span>
          </div>
        </div>

        {/* Online Devices */}
        <div
          onClick={() => navigate('/devices?status=online')}
          className="bg-[var(--panel)] border border-[var(--border)] hover:border-[var(--status-online)]/50 rounded-lg p-4 transition-all duration-150 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider">Online</span>
            <div className="p-2 rounded-md bg-[var(--status-online)]/10 text-[var(--status-online)]">
              <Activity className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums text-[var(--status-online)]">
              {formatNumber(metrics.onlineDevices)}
            </span>
            <span className="text-2xs text-[var(--status-online)] font-mono font-medium">
              {formatPercent((metrics.onlineDevices / (metrics.totalDevices || 1)) * 100)}
            </span>
          </div>
          <div className="mt-2 text-[2xs] text-[var(--text-muted)] flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--status-online)]" />
            <span>Active agent heartbeat</span>
          </div>
        </div>

        {/* Offline Devices */}
        <div
          onClick={() => navigate('/devices?status=offline')}
          className="bg-[var(--panel)] border border-[var(--border)] hover:border-[var(--status-offline)]/50 rounded-lg p-4 transition-all duration-150 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider">Offline</span>
            <div className="p-2 rounded-md bg-[var(--elevated)] text-[var(--status-offline)]">
              <WifiOff className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums text-[var(--status-offline)]">
              {formatNumber(metrics.offlineDevices)}
            </span>
            <span className="text-2xs text-[var(--text-muted)] font-mono">
              {formatPercent((metrics.offlineDevices / (metrics.totalDevices || 1)) * 100)}
            </span>
          </div>
          <div className="mt-2 text-[2xs] text-[var(--text-muted)] flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--status-offline)]" />
            <span>No heartbeat &gt; 5 min</span>
          </div>
        </div>

        {/* Needing Attention */}
        <div
          onClick={() => navigate('/devices?status=warning')}
          className="bg-[var(--panel)] border border-[var(--border)] hover:border-[var(--status-warning)]/50 rounded-lg p-4 transition-all duration-150 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider">Needing Attention</span>
            <div className="p-2 rounded-md bg-[var(--status-warning)]/10 text-[var(--status-warning)]">
              <AlertTriangle className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums text-[var(--status-warning)]">
              {formatNumber(metrics.needingAttention)}
            </span>
            <span className="text-2xs text-[var(--status-warning)] font-mono font-medium">
              Action Required
            </span>
          </div>
          <div className="mt-2 text-[2xs] text-[var(--text-muted)] flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--status-warning)]" />
            <span>Warning, Critical or Pending Reboot</span>
          </div>
        </div>

      </div>

      {/* ── Second Row: 4 Stat Cards (Alerts & Health) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Active Alerts */}
        <div
          onClick={() => navigate('/alerts')}
          className="bg-[var(--panel)] border border-[var(--border)] hover:border-[var(--accent)]/50 rounded-lg p-4 transition-all duration-150 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider">Active Alerts</span>
            <div className="p-2 rounded-md bg-[var(--elevated)] text-[var(--text-secondary)]">
              <ShieldAlert className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums text-[var(--text-primary)]">
              {formatNumber(metrics.activeAlerts)}
            </span>
            <Badge variant="warning" className="text-[2xs]">
              Open / Ack
            </Badge>
          </div>
          <div className="mt-2 text-[2xs] text-[var(--text-muted)]">
            Open incidents across managed endpoints
          </div>
        </div>

        {/* Critical Alerts */}
        <div
          onClick={() => navigate('/alerts?severity=critical')}
          className="bg-[var(--panel)] border border-[var(--border)] hover:border-[var(--status-critical)]/50 rounded-lg p-4 transition-all duration-150 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider">Critical Alerts</span>
            <div className="p-2 rounded-md bg-[var(--status-critical)]/10 text-[var(--status-critical)]">
              <ShieldAlert className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums text-[var(--status-critical)]">
              {formatNumber(metrics.criticalAlerts)}
            </span>
            <span className="text-2xs text-[var(--status-critical)] font-mono font-medium">High Severity</span>
          </div>
          <div className="mt-2 text-[2xs] text-[var(--text-muted)]">
            Requires immediate IT technician intervention
          </div>
        </div>

        {/* Patch Compliance */}
        <div
          onClick={() => navigate('/patches')}
          className="bg-[var(--panel)] border border-[var(--border)] hover:border-[var(--status-online)]/50 rounded-lg p-4 transition-all duration-150 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider">Patch Compliance</span>
            <div className="p-2 rounded-md bg-[var(--status-online)]/10 text-[var(--status-online)]">
              <ShieldCheck className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums text-[var(--status-online)]">
              {metrics.patchCompliance}%
            </span>
            <Badge variant={metrics.patchCompliance >= 85 ? 'online' : 'warning'} className="text-[2xs]">
              {metrics.patchCompliance >= 85 ? 'Compliant' : 'Needs Scan'}
            </Badge>
          </div>
          <div className="mt-2 text-[2xs] text-[var(--text-muted)]">
            OS & Application security patches applied
          </div>
        </div>

        {/* Pending Reboots */}
        <div
          onClick={() => navigate('/devices?pendingReboot=true')}
          className="bg-[var(--panel)] border border-[var(--border)] hover:border-[var(--status-warning)]/50 rounded-lg p-4 transition-all duration-150 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider">Pending Reboots</span>
            <div className="p-2 rounded-md bg-[var(--status-warning)]/10 text-[var(--status-warning)]">
              <RotateCcw className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums text-[var(--status-warning)]">
              {formatNumber(metrics.pendingReboots)}
            </span>
            <span className="text-2xs text-[var(--status-warning)] font-mono">Restart Pending</span>
          </div>
          <div className="mt-2 text-[2xs] text-[var(--text-muted)]">
            Post-patch update completion required
          </div>
        </div>

      </div>

      {/* ── Third Row: 4 Stat Cards (Operations & Activity) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Running Jobs */}
        <div
          onClick={() => navigate('/jobs')}
          className="bg-[var(--panel)] border border-[var(--border)] hover:border-[var(--accent)]/50 rounded-lg p-4 transition-all duration-150 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider">Running Jobs</span>
            <div className="p-2 rounded-md bg-[var(--accent)]/10 text-[var(--accent)]">
              <Play className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums text-[var(--text-primary)]">
              {formatNumber(metrics.runningJobs)}
            </span>
            <span className="text-2xs text-[var(--accent)] font-mono font-medium">Active Tasks</span>
          </div>
          <div className="mt-2 text-[2xs] text-[var(--text-muted)]">
            Automated scripts and patch deployments
          </div>
        </div>

        {/* Recently Enrolled */}
        <div
          onClick={() => navigate('/devices')}
          className="bg-[var(--panel)] border border-[var(--border)] hover:border-[var(--accent)]/50 rounded-lg p-4 transition-all duration-150 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider">Recently Enrolled</span>
            <div className="p-2 rounded-md bg-[var(--elevated)] text-[var(--text-secondary)]">
              <UserPlus className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums text-[var(--text-primary)]">
              {formatNumber(metrics.recentlyEnrolled)}
            </span>
            <span className="text-2xs text-[var(--text-muted)] font-mono">Last 7 Days</span>
          </div>
          <div className="mt-2 text-[2xs] text-[var(--text-muted)]">
            New agent installations onboarded
          </div>
        </div>

        {/* Active Remote Sessions */}
        <div
          onClick={() => navigate('/remote-sessions')}
          className="bg-[var(--panel)] border border-[var(--border)] hover:border-purple-500/50 rounded-lg p-4 transition-all duration-150 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider">Active Remote Sessions</span>
            <div className="p-2 rounded-md bg-purple-500/10 text-purple-400">
              <Monitor className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums text-purple-400">
              {formatNumber(metrics.activeRemoteSessions)}
            </span>
            <span className="text-2xs text-purple-400 font-mono">Live Control</span>
          </div>
          <div className="mt-2 text-[2xs] text-[var(--text-muted)]">
            Technician remote desktop / shell sessions
          </div>
        </div>

        {/* Antivirus Status */}
        <div
          onClick={() => navigate('/software')}
          className="bg-[var(--panel)] border border-[var(--border)] hover:border-[var(--status-online)]/50 rounded-lg p-4 transition-all duration-150 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wider">Antivirus Status</span>
            <div className="p-2 rounded-md bg-[var(--status-online)]/10 text-[var(--status-online)]">
              <ShieldCheck className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-3xl font-bold tabular-nums text-[var(--status-online)]">
              {metrics.antivirusActive}%
            </span>
            <Badge variant={metrics.antivirusActive >= 90 ? 'online' : 'warning'} className="text-[2xs]">
              Protected
            </Badge>
          </div>
          <div className="mt-2 text-[2xs] text-[var(--text-muted)]">
            Endpoints with active endpoint security
          </div>
        </div>

      </div>

      {/* ── Resource Utilization Gauges (CPU / Memory / Disk Summary) ── */}
      <div className="bg-[var(--panel)] border border-[var(--border)] rounded-lg p-5 space-y-4">
        <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
          <div className="flex items-center gap-2">
            <Cpu className="h-4 w-4 text-[var(--accent)]" />
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Average Hardware Utilization</h2>
            <span className="text-xs text-[var(--text-muted)] font-mono">(Online Endpoints)</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/devices')} className="text-xs text-[var(--accent)] hover:underline">
            View All Performance Logs
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-1">
          
          {/* Average CPU */}
          <div className="bg-[var(--bg)] border border-[var(--border)] rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-[var(--text-secondary)] flex items-center gap-1.5">
                <Cpu className="h-3.5 w-3.5 text-[var(--accent)]" />
                <span>Processor Load (CPU)</span>
              </span>
              <span className="font-bold tabular-nums text-[var(--text-primary)] text-sm">
                {metrics.avgCpu}%
              </span>
            </div>
            
            <div className="w-full h-2.5 bg-[var(--elevated)] rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all duration-500 rounded-full',
                  metrics.avgCpu > 85 ? 'bg-[var(--status-critical)]' : metrics.avgCpu > 70 ? 'bg-[var(--status-warning)]' : 'bg-[var(--accent)]'
                )}
                style={{ width: `${metrics.avgCpu}%` }}
              />
            </div>

            <div className="flex items-center justify-between text-[2xs] text-[var(--text-muted)]">
              <span>Optimal range</span>
              <span>{metrics.avgCpu < 70 ? 'Healthy System Load' : 'High Processing Demand'}</span>
            </div>
          </div>

          {/* Average Memory */}
          <div className="bg-[var(--bg)] border border-[var(--border)] rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-[var(--text-secondary)] flex items-center gap-1.5">
                <Database className="h-3.5 w-3.5 text-purple-400" />
                <span>Memory Allocation (RAM)</span>
              </span>
              <span className="font-bold tabular-nums text-[var(--text-primary)] text-sm">
                {metrics.avgMemory}%
              </span>
            </div>
            
            <div className="w-full h-2.5 bg-[var(--elevated)] rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all duration-500 rounded-full',
                  metrics.avgMemory > 85 ? 'bg-[var(--status-critical)]' : metrics.avgMemory > 70 ? 'bg-[var(--status-warning)]' : 'bg-purple-500'
                )}
                style={{ width: `${metrics.avgMemory}%` }}
              />
            </div>

            <div className="flex items-center justify-between text-[2xs] text-[var(--text-muted)]">
              <span>Avg 16 GB allocated</span>
              <span>{metrics.avgMemory > 80 ? 'Memory Pressure Warning' : 'Normal Usage'}</span>
            </div>
          </div>

          {/* Average Disk */}
          <div className="bg-[var(--bg)] border border-[var(--border)] rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-[var(--text-secondary)] flex items-center gap-1.5">
                <HardDrive className="h-3.5 w-3.5 text-emerald-400" />
                <span>System Disk Space</span>
              </span>
              <span className="font-bold tabular-nums text-[var(--text-primary)] text-sm">
                {metrics.avgDisk}%
              </span>
            </div>
            
            <div className="w-full h-2.5 bg-[var(--elevated)] rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all duration-500 rounded-full',
                  metrics.avgDisk > 85 ? 'bg-[var(--status-critical)]' : metrics.avgDisk > 70 ? 'bg-[var(--status-warning)]' : 'bg-emerald-500'
                )}
                style={{ width: `${metrics.avgDisk}%` }}
              />
            </div>

            <div className="flex items-center justify-between text-[2xs] text-[var(--text-muted)]">
              <span>System Volume C:\</span>
              <span>{metrics.avgDisk > 85 ? 'Low Disk Space Alert' : 'Sufficient Storage'}</span>
            </div>
          </div>

        </div>
      </div>

      {/* ── Middle Section: 14-Day Alert Trend Chart & Device Status Distribution ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* 14-Day Alert Trend Bar Chart */}
        <div className="lg:col-span-2 bg-[var(--panel)] border border-[var(--border)] rounded-lg p-5 flex flex-col justify-between space-y-4">
          <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
            <div>
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">14-Day Incident Alert Trend</h2>
              <p className="text-[2xs] text-[var(--text-muted)]">Daily frequency of critical, warning, and resolved events</p>
            </div>
            
            <div className="flex items-center gap-3 text-[2xs]">
              <span className="flex items-center gap-1 text-[var(--status-critical)]">
                <span className="h-2 w-2 rounded-full bg-[var(--status-critical)]" />
                Critical
              </span>
              <span className="flex items-center gap-1 text-[var(--status-warning)]">
                <span className="h-2 w-2 rounded-full bg-[var(--status-warning)]" />
                Warning
              </span>
              <span className="flex items-center gap-1 text-[var(--status-online)]">
                <span className="h-2 w-2 rounded-full bg-[var(--status-online)]" />
                Resolved
              </span>
            </div>
          </div>

          {/* Custom Styled HTML/Tailwind Bar Chart */}
          <div className="pt-4 pb-2">
            <div className="h-44 flex items-end justify-between gap-1.5 sm:gap-2 px-1 relative">
              
              {/* Background Horizontal Scale Lines */}
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-20">
                <div className="border-b border-[var(--border)] w-full" />
                <div className="border-b border-[var(--border)] w-full" />
                <div className="border-b border-[var(--border)] w-full" />
              </div>

              {(metrics.alertTrend ?? []).map((day, idx) => {
                const totalVal = day.critical + day.warning + day.resolved;
                const criticalPct = (day.critical / maxTrendVal) * 100;
                const warningPct = (day.warning / maxTrendVal) * 100;
                const resolvedPct = (day.resolved / maxTrendVal) * 100;
                const dateLabel = new Date(day.date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });

                return (
                  <div
                    key={day.date}
                    className="flex-1 flex flex-col items-center group relative h-full justify-end"
                    onMouseEnter={() => setHoveredBarIndex(idx)}
                    onMouseLeave={() => setHoveredBarIndex(null)}
                  >
                    {/* Hover Tooltip Popup */}
                    {hoveredBarIndex === idx && (
                      <div className="absolute bottom-full mb-2 bg-[var(--elevated)] border border-[var(--border)] rounded p-2 text-[2xs] z-20 shadow-xl whitespace-nowrap space-y-1">
                        <div className="font-mono font-bold text-[var(--text-primary)] border-b border-[var(--border)] pb-1">
                          {day.date}
                        </div>
                        <div className="text-[var(--status-critical)]">Critical: {day.critical}</div>
                        <div className="text-[var(--status-warning)]">Warning: {day.warning}</div>
                        <div className="text-[var(--status-online)]">Resolved: {day.resolved}</div>
                      </div>
                    )}

                    {/* Stacked Bars */}
                    <div className="w-full max-w-[20px] rounded-t flex flex-col justify-end overflow-hidden bg-[var(--elevated)]/40 hover:brightness-125 transition-all cursor-pointer">
                      <div
                        style={{ height: `${criticalPct}%` }}
                        className="w-full bg-[var(--status-critical)] transition-all"
                      />
                      <div
                        style={{ height: `${warningPct}%` }}
                        className="w-full bg-[var(--status-warning)] transition-all"
                      />
                      <div
                        style={{ height: `${resolvedPct}%` }}
                        className="w-full bg-[var(--status-online)] transition-all"
                      />
                    </div>

                    {/* Date Label */}
                    <span className="text-[2xs] text-[var(--text-muted)] font-mono mt-2 truncate w-full text-center">
                      {dateLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="pt-2 border-t border-[var(--border)] flex items-center justify-between text-[2xs] text-[var(--text-muted)]">
            <span>Hover bars for detailed daily metrics</span>
            <button
              onClick={() => navigate('/alerts')}
              className="text-[var(--accent)] hover:underline flex items-center gap-1 font-medium"
            >
              <span>View Full Alert History</span>
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Device Status Distribution */}
        <div className="bg-[var(--panel)] border border-[var(--border)] rounded-lg p-5 flex flex-col justify-between space-y-4">
          <div className="border-b border-[var(--border)] pb-3">
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Device Status Distribution</h2>
            <p className="text-[2xs] text-[var(--text-muted)]">Endpoints segmented by agent state</p>
          </div>

          {/* Horizontal Combined Distribution Bar */}
          <div className="space-y-4">
            <div className="h-4 w-full bg-[var(--elevated)] rounded-md overflow-hidden flex">
              <div
                style={{ width: `${(metrics.onlineDevices / (metrics.totalDevices || 1)) * 100}%` }}
                className="h-full bg-[var(--status-online)]"
                title={`Online: ${metrics.onlineDevices}`}
              />
              <div
                style={{ width: `${(metrics.warningDevices / (metrics.totalDevices || 1)) * 100}%` }}
                className="h-full bg-[var(--status-warning)]"
                title={`Warning: ${metrics.warningDevices}`}
              />
              <div
                style={{ width: `${(metrics.offlineDevices / (metrics.totalDevices || 1)) * 100}%` }}
                className="h-full bg-[var(--status-offline)]"
                title={`Offline: ${metrics.offlineDevices}`}
              />
              <div
                style={{ width: `${(metrics.criticalDevices / (metrics.totalDevices || 1)) * 100}%` }}
                className="h-full bg-[var(--status-critical)]"
                title={`Critical: ${metrics.criticalDevices}`}
              />
            </div>

            {/* List Breakdown Rows */}
            <div className="space-y-2.5 pt-1">
              
              <div
                onClick={() => navigate('/devices?status=online')}
                className="p-2 rounded-md hover:bg-[var(--elevated)] flex items-center justify-between text-xs cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[var(--status-online)]" />
                  <span className="font-medium text-[var(--text-primary)]">Online</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold tabular-nums text-[var(--text-primary)]">{metrics.onlineDevices}</span>
                  <span className="text-[2xs] text-[var(--text-muted)] font-mono w-10 text-right">
                    {formatPercent((metrics.onlineDevices / (metrics.totalDevices || 1)) * 100)}
                  </span>
                </div>
              </div>

              <div
                onClick={() => navigate('/devices?status=warning')}
                className="p-2 rounded-md hover:bg-[var(--elevated)] flex items-center justify-between text-xs cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[var(--status-warning)]" />
                  <span className="font-medium text-[var(--text-primary)]">Warning</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold tabular-nums text-[var(--text-primary)]">{metrics.warningDevices}</span>
                  <span className="text-[2xs] text-[var(--text-muted)] font-mono w-10 text-right">
                    {formatPercent((metrics.warningDevices / (metrics.totalDevices || 1)) * 100)}
                  </span>
                </div>
              </div>

              <div
                onClick={() => navigate('/devices?status=offline')}
                className="p-2 rounded-md hover:bg-[var(--elevated)] flex items-center justify-between text-xs cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[var(--status-offline)]" />
                  <span className="font-medium text-[var(--text-primary)]">Offline</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold tabular-nums text-[var(--text-primary)]">{metrics.offlineDevices}</span>
                  <span className="text-[2xs] text-[var(--text-muted)] font-mono w-10 text-right">
                    {formatPercent((metrics.offlineDevices / (metrics.totalDevices || 1)) * 100)}
                  </span>
                </div>
              </div>

              <div
                onClick={() => navigate('/devices?status=critical')}
                className="p-2 rounded-md hover:bg-[var(--elevated)] flex items-center justify-between text-xs cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[var(--status-critical)]" />
                  <span className="font-medium text-[var(--text-primary)]">Critical</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold tabular-nums text-[var(--text-primary)]">{metrics.criticalDevices}</span>
                  <span className="text-[2xs] text-[var(--text-muted)] font-mono w-10 text-right">
                    {formatPercent((metrics.criticalDevices / (metrics.totalDevices || 1)) * 100)}
                  </span>
                </div>
              </div>

            </div>
          </div>

          <div className="pt-2 border-t border-[var(--border)]">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/devices')}
              className="w-full text-xs justify-center"
            >
              Manage Device Fleet
              <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>

      </div>

      {/* ── Bottom Grid: Activity Feed & Recently Enrolled Devices Table ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Activity Feed Section */}
        <div className="bg-[var(--panel)] border border-[var(--border)] rounded-lg p-5 space-y-4 flex flex-col justify-between">
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[var(--border)] pb-3">
              <div>
                <h2 className="text-sm font-semibold text-[var(--text-primary)]">Live Activity Feed</h2>
                <p className="text-[2xs] text-[var(--text-muted)]">Real-time system events</p>
              </div>

              {/* Filter Tabs */}
              <div className="flex items-center gap-1 bg-[var(--bg)] p-1 rounded-md border border-[var(--border)]">
                <button
                  onClick={() => setActivityFilter('all')}
                  className={cn(
                    'px-2 py-0.5 text-[2xs] rounded font-medium transition-colors',
                    activityFilter === 'all'
                      ? 'bg-[var(--accent)] text-white'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                  )}
                >
                  All
                </button>
                <button
                  onClick={() => setActivityFilter('alert')}
                  className={cn(
                    'px-2 py-0.5 text-[2xs] rounded font-medium transition-colors',
                    activityFilter === 'alert'
                      ? 'bg-[var(--accent)] text-white'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                  )}
                >
                  Alerts
                </button>
                <button
                  onClick={() => setActivityFilter('job')}
                  className={cn(
                    'px-2 py-0.5 text-[2xs] rounded font-medium transition-colors',
                    activityFilter === 'job'
                      ? 'bg-[var(--accent)] text-white'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                  )}
                >
                  Jobs
                </button>
              </div>
            </div>

            {/* Scrollable Feed List */}
            <div className="mt-4 space-y-3 max-h-[380px] overflow-y-auto pr-1">
              {filteredActivityFeed.length === 0 ? (
                <div className="text-center py-12 text-xs text-[var(--text-muted)]">
                  No activity events matching selected filter.
                </div>
              ) : (
                filteredActivityFeed.map((item) => (
                  <div
                    key={item.id}
                    className="p-3 rounded-lg bg-[var(--bg)] border border-[var(--border)] hover:border-[var(--border-strong)] transition-all space-y-1.5"
                  >
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        {getActivityIcon(item.type)}
                        <span className="font-medium text-[var(--text-primary)] line-clamp-1">{item.message}</span>
                      </div>
                      <span className="text-[2xs] text-[var(--text-muted)] shrink-0 font-mono">
                        {timeAgo(item.timestamp)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[2xs] text-[var(--text-muted)] pt-0.5">
                      {item.hostname && (
                        <span className="font-mono text-[var(--text-secondary)] bg-[var(--elevated)] px-1.5 py-0.5 rounded border border-[var(--border)]">
                          {item.hostname}
                        </span>
                      )}
                      {item.userName && (
                        <span>By {item.userName}</span>
                      )}
                      <Badge variant={item.severity === 'critical' ? 'critical' : item.severity === 'warning' ? 'warning' : 'default'} className="ml-auto text-[2xs]">
                        {item.severity}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="pt-3 border-t border-[var(--border)]">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/audit-log')}
              className="w-full text-xs text-[var(--accent)] hover:underline justify-center"
            >
              View Complete Audit & Log Register
            </Button>
          </div>
        </div>

        {/* Recently Enrolled Devices Table */}
        <div className="lg:col-span-2 bg-[var(--panel)] border border-[var(--border)] rounded-lg p-5 flex flex-col justify-between space-y-4">
          <div>
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
              <div>
                <h2 className="text-sm font-semibold text-[var(--text-primary)]">Recently Enrolled Devices</h2>
                <p className="text-[2xs] text-[var(--text-muted)]">Latest endpoints onboarded to InsightNode RMM</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/devices')}
                className="text-xs text-[var(--accent)] hover:underline"
              >
                View All Devices ({metrics.totalDevices})
              </Button>
            </div>

            {/* Table */}
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-[var(--border)] text-[var(--text-muted)] uppercase text-[2xs] font-medium tracking-wider">
                    <th className="pb-2.5 font-medium">Hostname</th>
                    <th className="pb-2.5 font-medium">Client & Site</th>
                    <th className="pb-2.5 font-medium">Operating System</th>
                    <th className="pb-2.5 font-medium">Enrolled Date</th>
                    <th className="pb-2.5 font-medium text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {recentDevices.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-xs text-[var(--text-muted)]">
                        No recently enrolled devices found.
                      </td>
                    </tr>
                  ) : (
                    recentDevices.map((device) => (
                      <tr
                        key={device.id}
                        onClick={() => navigate(`/devices/${device.id}`)}
                        className="hover:bg-[var(--elevated)] cursor-pointer transition-colors group"
                      >
                        <td className="py-3 font-medium text-[var(--text-primary)] font-mono group-hover:text-[var(--accent)]">
                          {device.hostname}
                        </td>
                        <td className="py-3 text-[var(--text-secondary)]">
                          <div>{device.client}</div>
                          <div className="text-[2xs] text-[var(--text-muted)]">{device.site}</div>
                        </td>
                        <td className="py-3 text-[var(--text-secondary)] truncate max-w-[150px]" title={device.os}>
                          {device.os}
                        </td>
                        <td className="py-3 text-[var(--text-muted)] font-mono text-[2xs]">
                          {formatDate(device.enrolledAt, 'MMM d, yyyy')}
                        </td>
                        <td className="py-3 text-right">
                          <Badge variant={device.status} dot>
                            {device.status}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pt-3 border-t border-[var(--border)] flex items-center justify-between text-xs text-[var(--text-muted)]">
            <span>Showing top 5 newly deployed agents</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/automation')}
              className="text-xs"
            >
              <UserPlus className="h-3.5 w-3.5 mr-1.5" />
              Enroll New Endpoint
            </Button>
          </div>
        </div>

      </div>

      {/* ── Bottom Operational Shortcuts Banner ── */}
      <div className="bg-[var(--panel)] border border-[var(--border)] rounded-lg p-4 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-[var(--accent)]/15 border border-[var(--accent)]/30 text-[var(--accent)]">
            <Zap className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-xs font-semibold text-[var(--text-primary)]">Quick Management Workflows</h3>
            <p className="text-[2xs] text-[var(--text-muted)]">Execute immediate administrative tasks across all tenants</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/scripts')}
            className="text-xs"
          >
            <Terminal className="h-3.5 w-3.5 mr-1.5 text-[var(--accent)]" />
            Run PowerShell Script
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/patches')}
            className="text-xs"
          >
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5 text-[var(--status-online)]" />
            Trigger Patch Scan
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/reports')}
            className="text-xs"
          >
            <FileCode className="h-3.5 w-3.5 mr-1.5 text-purple-400" />
            Generate Health Report
          </Button>
        </div>
      </div>

    </div>
  );
}

export default Overview;
